import MomentUtils from '@date-io/moment';
import AdapterMoment from '@mui/lab/AdapterMoment';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { CssBaseline, Grid, StyledEngineProvider, Theme, ThemeProvider, Typography } from '@mui/material';
import AuthService from "api/Auth";
import appMainTheme from 'assets/appMainTheme';
import { AppImages } from 'assets/appResources';
import { AxiosHandler } from 'components/CallServer';
import { StartupSpinnertIndicator } from 'components/CallServer/StartupSpinnertIndicator';
import { SuspenseRequestWrapIndicator } from 'components/CallServer/SuspenseRequestWrapIndicator';
import { ReactSplitString } from 'components/ReactSplitString';
import SnackbarProvider from 'components/SnackbarProvider/SnackbarProvider';
import { ConnectedRouter } from 'connected-react-router';
import ErrorBoundary from 'ErrorBoundary';
import { createBrowserHistory } from 'history';
import { AccountLoginResult } from 'models/ApiEnums';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/pt';
import * as React from 'react';
import { Translation } from 'react-i18next';
import { Provider } from 'react-redux';
import Session from 'services/Session';
import configureStore from 'store/configureStore';
import { ApplicationState } from "store/rootReducer";
import Routes from '../routes';
//////////////////////////////////////////////////////////////////////////////////

interface IState {
    theme: Theme;
    currentLocale: string;
    loading: boolean;
    lastError: any;
}

interface IProps {
}

class LocalizedUtils extends MomentUtils {
    dateFormat: string = "L";
}

//////////////////////////////////////////////////////////////////////////////////

export default class ScreensRoot extends React.PureComponent<IProps, IState> {

    private initialState: ApplicationState;
    private history: any;
    private store: any;

    ///
    constructor(props: IProps) {
        super(props);
        this.state = { loading: true, lastError: null, theme: appMainTheme(Session.appTheme.themeName, Session.uiCulture), currentLocale: Session.locale };
        this.history = createBrowserHistory();
        this.initialState = {} as ApplicationState;
        this.store = configureStore(this.history, this.initialState);
        moment.locale(this.state.currentLocale);
    }

    ///
    private handleUserInfoChanges = () => {
        const locale = Session.locale;
        moment.locale(locale);
        this.setState({ theme: appMainTheme(Session.appTheme.themeName, Session.uiCulture), currentLocale: locale });
    };

    ///
    private startup() {
        // Observa as mudanças de Tema e Cultura.
        Session.subscribe(this.handleUserInfoChanges);

        // ** Não alterar esta ordem de inicialização das constantes **
        const isConnected = Session.isConnected;
        const tokenValidTo = Session.tokenValidTo;
        const refreshToken = Session.refreshToken;
        const now = new Date();

        // Solicitar login novamente?
        if (!isConnected && window.opener == null) {
            Session.clearSession();
        }

        /// Configurações da aplicação
        const authService = new AuthService();
        authService
            .getSettings()
            .then((settings) => {
                if (settings.releaseNumber != null && Session.checkNewSystemVersion(settings.releaseNumber)) {
                    this.setState({ lastError: "NewRelease" });
                }
                else {
                    Session.storeSettings(settings);

                    // Atribui Metadados no HEAD
                    window.document.title = settings.theme.title;
                    document.getElementById("og-title")?.setAttribute("content", settings.theme.title);
                    document.getElementById("og-image")?.setAttribute("content", `${document.location.protocol}//${document.location.host}/api/v1.0/Session/Logo`);

                    if (isConnected) {
                        // Verifica se o usuário ainda está com o token de autenticação válido.
                        if (window.opener == null && tokenValidTo < now) {
                            // O token expirou, então vamos tentar login pelo RefreshToken.
                            const refreshTokenValidate = refreshToken ? new Date(refreshToken.expirationDate) : now;
                            if (refreshTokenValidate > now) {
                                // Refresh Token Válido. Tenta re-autenticar.
                                authService.loginFromRefreshToken(refreshToken.token)
                                    .then((result) => {
                                        if (result.status == AccountLoginResult.Valid) {
                                            // Autorizado: reprocessa token.
                                            Session.processNewSession(result.webToken);
                                        }
                                    })
                                    .finally(() => {
                                        this.setState({ loading: false });
                                    });
                            }
                            else {
                                // Refresh Token Inválido.
                                this.setState({ loading: false });
                            }
                        }
                        else {
                            // Token ainda válido, atualiza data de último acesso ao sistema.
                            authService
                                .updateLastLogin()
                                .then((authorized) => {
                                    if (!authorized) {
                                        Session.clearSession();
                                    }
                                })
                                .catch(() => {
                                    Session.clearSession();
                                })
                                .finally(() => {
                                    this.setState({ loading: false });
                                });
                        }
                    }
                    else {
                        this.setState({ loading: false });
                    }
                }
            })
            .catch((error) => {
                // Erro ao lêr a configuração inicial do sistema.
                this.setState({ loading: false, lastError: error });
            });
    }

    ///
    public componentWillUnmount() {
        Session.unsubscribe(this.handleUserInfoChanges);
    }

    ///
    public componentDidMount() {
        this.startup();
    }

    ///
    public render() {

        if (this.state.lastError == "NewRelease") {
            setTimeout(() => {
                window.location.reload();
                //Mudar para o código abaixo se não funcionar.
                //window.location.href = window.location.href,
            }, 2000);
        }

        return (
            <StyledEngineProvider injectFirst>
                <CssBaseline />
                <LocalizationProvider dateAdapter={AdapterMoment} locale={this.state.currentLocale}>
                    <Provider store={this.store}>
                        <ConnectedRouter history={this.history}>
                            <React.Suspense fallback={<SuspenseRequestWrapIndicator />}>
                                <ThemeProvider theme={this.state.theme}>
                                    <SnackbarProvider
                                        SnackbarProps={
                                            {
                                                anchorOrigin: { horizontal: "right", vertical: "bottom" },
                                            }
                                        }
                                    >
                                        <AxiosHandler></AxiosHandler>
                                        {
                                            this.state.lastError || (this.state.loading && window.opener == null && document.location.pathname.toLowerCase().indexOf('/mobile/editor') == -1)
                                                ? (
                                                    <Grid container justifyContent="center" alignItems="center" style={{ height: "100vh" }}>
                                                        <Grid item>
                                                            <Grid container justifyContent="center" alignItems="center" wrap="nowrap">
                                                                <Grid item>
                                                                    <img src={AppImages.TheGenieRight} alt="Genie Logo" />
                                                                </Grid>
                                                                <Grid item xs>
                                                                    {
                                                                        this.state.lastError
                                                                            ? (
                                                                                <Translation>
                                                                                    {
                                                                                        (t) => <Typography variant="h4" color="error">
                                                                                            {
                                                                                                this.state.lastError == "NewRelease"
                                                                                                    ? <ReactSplitString text={t("Startup.NewReleaseDetected")} splitChar="$" separator={<br />} />
                                                                                                    : t("Startup.InitializeFailed")
                                                                                            }
                                                                                        </Typography>
                                                                                    }
                                                                                </Translation>
                                                                            )
                                                                            : (
                                                                                <>
                                                                                    <StartupSpinnertIndicator />
                                                                                    <Translation>
                                                                                        {
                                                                                            (t) => <span>{t("Common.Literals.Initializing")}</span>
                                                                                        }
                                                                                    </Translation>
                                                                                </>
                                                                            )
                                                                    }
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                )
                                                : !this.state.loading
                                                    ? <ErrorBoundary><Routes /></ErrorBoundary>
                                                    : null
                                        }
                                    </SnackbarProvider>
                                </ThemeProvider>
                            </React.Suspense>
                        </ConnectedRouter>
                    </Provider>
                </LocalizationProvider>
            </StyledEngineProvider>
        );
    }
}